/**
 * InsightsSection.tsx — the ONE admin "Insights" section (UX Wave 3c): the
 * merge of the old "Analytics" and "Earnings" sidebar items into a single
 * section with two sub-views under ONE shared window selector.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: UX Wave 3c (Insights +
 * audit tracer, 2026-07-21).
 *
 * WHY one section: the CTO's Wave-3 IA feedback (§4) — analytics and earnings
 * answer the same "how is the company doing?" question, so they live behind
 * one sidebar item. Sub-views on the shared WAI-ARIA kit Tabs: **Company
 * analytics** (the money-free heatmap + per-clinician activity panel) and
 * **Earnings** (the ADMIN-only money table with its inline rate editor). The
 * active sub-view is URL-synced (`?view=`) so a deep link lands on the right
 * table; the retired `?tab=earnings` key redirects here with `view=earnings`
 * pinned (AdminPage owns the rewrite) — until that rewrite runs, the legacy
 * `tab` param itself picks the sub-view so the first paint is already right.
 *
 * PERIODS SPLIT (2026-08-26): the Wave-3c shared `?days` window now drives
 * ONLY Company analytics (whose reads still speak days); the Earnings
 * sub-view carries its OWN calendar month picker (this month to date / past
 * 12 months / custom range → `?from=&to=`, see EarningsTab + lib/monthRange)
 * because compensation is settled per calendar month, not per rolling
 * window. The days selector HIDES on the Earnings view rather than sitting
 * inert beside a second period control.
 *
 * Money rules: the Earnings sub-view is the ADMIN money surface (clinicians
 * never reach /admin — R41; since the 2026-08-26 CTO decision a clinician
 * sees their OWN earnings on their My-stats card, never this table or the
 * company total); the Company analytics sub-view stays money-free COUNTS,
 * exactly as the clinician-visible My stats tab renders them.
 */
import { useSearchParams } from 'react-router-dom';
import { Select } from '../ui/kit';
import { Tabs, TabPanel } from '../ui/Tabs';
import { AnalyticsTab } from './AnalyticsTab';
import { EarningsTab } from './EarningsTab';

type InsightsView = 'analytics' | 'earnings';

const VIEWS: Array<{ id: InsightsView; label: string }> = [
  { id: 'analytics', label: 'Company analytics' },
  { id: 'earnings', label: 'Earnings' },
];

/** The analytics window options — the same set the old Earnings selector offered. */
const WINDOWS = [30, 60, 90];
const DEFAULT_WINDOW = 30;

function isView(value: string | null): value is InsightsView {
  return value === 'analytics' || value === 'earnings';
}

export function InsightsSection() {
  const [searchParams, setSearchParams] = useSearchParams();

  // The active sub-view lives in the URL (`?view=`). Fallback order: an
  // explicit valid `view` wins; else the RETIRED `?tab=earnings` deep link
  // (pre-rewrite render) picks Earnings; else Company analytics.
  const viewParam = searchParams.get('view');
  const view: InsightsView = isView(viewParam) ? viewParam : searchParams.get('tab') === 'earnings' ? 'earnings' : 'analytics';

  // The analytics window is URL-synced too (`?window=`), so a copied link
  // shows the same period; anything unknown falls back to the 30-day default.
  const windowParam = Number(searchParams.get('window'));
  const days = WINDOWS.includes(windowParam) ? windowParam : DEFAULT_WINDOW;

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    next.set(key, value);
    setSearchParams(next, { replace: true });
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--gps-s-3)' }}>
      {/* The days window drives ONLY Company analytics (2026-08-26) — the
          Earnings sub-view has its own calendar month picker, so this selector
          hides there rather than sitting inert beside it. */}
      {view === 'analytics' ? (
        <div style={{ display: 'flex', gap: 'var(--gps-s-2)', alignItems: 'center', justifyContent: 'flex-end' }}>
          <label htmlFor="insights-window" style={{ fontSize: '13px', color: 'var(--gps-muted)', fontWeight: 600 }}>
            Window
          </label>
          <Select
            id="insights-window"
            value={days}
            onChange={(e) => setParam('window', e.target.value)}
            style={{ width: 'auto', minHeight: '36px', padding: '4px 8px' }}
          >
            {WINDOWS.map((w) => (
              <option key={w} value={w}>
                Last {w} days
              </option>
            ))}
          </Select>
        </div>
      ) : null}

      <div>
        <Tabs label="Insights views" tabs={VIEWS} activeId={view} onChange={(id) => setParam('view', id)} idBase="insights" />
        <TabPanel idBase="insights" tabId="analytics" activeId={view}>
          <AnalyticsTab days={days} />
        </TabPanel>
        <TabPanel idBase="insights" tabId="earnings" activeId={view}>
          <EarningsTab />
        </TabPanel>
      </div>
    </div>
  );
}
